import { Controller } from "@hotwired/stimulus"
import { replayAnimation } from "../lib/motion"
import { createLogger } from "../lib/logger"

const log = createLogger('DistrictTargeting')

/**
 * District Targeting Controller
 * 행정구역 기반 광고 타겟팅 (Phase 1 migration: GPS → 行政구역)
 *
 * Features:
 * - City/District cascading selection
 * - Multi-district checkbox selection
 * - Nationwide toggle (disables district selection)
 * - Selected count display below district grid
 * - Dynamic hidden field management for form submission
 *
 * Usage:
 * <div data-controller="district-targeting"
 *      data-district-targeting-districts-url-value="/api/districts">
 *   <input type="checkbox" data-district-targeting-target="nationwideCheckbox"
 *          data-action="change->district-targeting#toggleNationwide">
 *   <div data-district-targeting-target="districtSection">
 *     <select data-district-targeting-target="citySelect"
 *             data-action="change->district-targeting#loadDistricts">
 *     <div data-district-targeting-target="districtContainer">
 *     <p data-district-targeting-target="selectedCount">
 *     <div data-district-targeting-target="hiddenFields">
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    "nationwideCheckbox",   // Nationwide toggle checkbox
    "districtSection",      // Container for district selection (hidden when nationwide)
    "citySelect",           // City dropdown
    "districtContainer",    // District checkbox grid
    "selectedCount",        // Selected count text (shown below district grid)
    "hiddenFields",         // Hidden inputs container
    "warningMessage",       // No selection warning
    "reachMeter",           // Decorative honeycomb (ads/wizard/_reach_meter): one tile per allowed district
    "reachTile"
  ]

  static values = {
    districtsUrl: { type: String, default: "/api/districts" },
    subscriptionPlan: { type: String, default: "" },      // Phase 4: 플랜 인식
    nationwideAllowed: { type: Boolean, default: false }, // Phase 4: 전국 타겟팅 허용 여부
    maxDistricts: { type: Number, default: 3 },           // Phase 4: 티어별 최대 district 수
    fieldName: { type: String, default: "ad[target_district_ids][]" } // Configurable field name for form submission
  }

  connect() {
    this.selectedDistricts = new Map() // id => { id, name, city }

    // Initialize from existing hidden fields
    this.initializeFromHiddenFields()

    log.info('District targeting connected', {
      selectedCount: this.selectedDistricts.size,
      subscriptionPlan: this.subscriptionPlanValue,
      nationwideAllowed: this.nationwideAllowedValue
    })
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  initializeFromHiddenFields() {
    if (!this.hasHiddenFieldsTarget) return

    const hiddenInputs = this.hiddenFieldsTarget.querySelectorAll('input[type="hidden"]')
    hiddenInputs.forEach(input => {
      const id = parseInt(input.value, 10)
      if (!isNaN(id) && id > 0) {
        this.selectedDistricts.set(id, { id, name: `District ${id}` })
      }
    })
  }

  // ============================================================================
  // Nationwide Toggle
  // ============================================================================

  toggleNationwide() {
    // Phase 4: Security check - prevent nationwide enable if not allowed by plan
    if (!this.nationwideAllowedValue && this.nationwideCheckboxTarget.checked) {
      log.warn('Nationwide blocked - not allowed for current plan', {
        subscriptionPlan: this.subscriptionPlanValue
      })
      // Force uncheck and keep disabled (shouldn't happen if HTML disabled is set)
      this.nationwideCheckboxTarget.checked = false
      this.nationwideCheckboxTarget.disabled = true
      return
    }

    const isNationwide = this.nationwideCheckboxTarget.checked

    if (this.hasDistrictSectionTarget) {
      this.districtSectionTarget.classList.toggle('hidden', isNationwide)
    }

    if (this.hasWarningMessageTarget) {
      this.warningMessageTarget.classList.add('hidden')
    }

    log.info('Nationwide toggled', { isNationwide, subscriptionPlan: this.subscriptionPlanValue })
  }

  // ============================================================================
  // City Selection → Load Districts
  // ============================================================================

  async loadDistricts() {
    const city = this.citySelectTarget.value

    if (!city) {
      this.renderDistrictPlaceholder()
      return
    }

    try {
      const url = `${this.districtsUrlValue}?city=${encodeURIComponent(city)}`
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const districts = await response.json()
      this.renderDistrictCheckboxes(districts)

      log.info('Districts loaded', { city, count: districts.length })
    } catch (error) {
      log.error('Failed to load districts', error)
      this.renderDistrictError()
    }
  }

  // ============================================================================
  // District Checkbox Rendering
  // ============================================================================

  renderDistrictPlaceholder() {
    if (!this.hasDistrictContainerTarget) return

    this.districtContainerTarget.innerHTML = `
      <p class="text-sm text-charcoal-400 text-center py-8">
        請先選擇縣市
      </p>
    `
  }

  renderDistrictError() {
    if (!this.hasDistrictContainerTarget) return

    this.districtContainerTarget.innerHTML = `
      <p class="text-sm text-red-500 text-center py-8">
        載入區域失敗，請重試
      </p>
    `
  }

  renderDistrictCheckboxes(districts) {
    if (!this.hasDistrictContainerTarget) return

    if (districts.length === 0) {
      this.districtContainerTarget.innerHTML = `
        <p class="text-sm text-charcoal-400 text-center py-8">
          此縣市沒有可選擇的區域
        </p>
      `
      return
    }

    // Phase 4: 한도 도달 여부 확인
    const atLimit = this.selectedDistricts.size >= this.maxDistrictsValue

    // Ad targeting (has a reach meter): chips cascade in (--i) and fill with the plan colour when chosen
    // (components/ad_plan_cards.css). Elsewhere (search filter) the plain checkbox list stays.
    const chips = this.hasReachMeterTarget
    const checkboxesHtml = districts.map((district, index) => {
      const isSelected = this.selectedDistricts.has(district.id)
      const checkedAttr = isSelected ? 'checked' : ''
      // 한도 도달 시 미선택 체크박스 비활성화
      const disabledAttr = (!isSelected && atLimit) ? 'disabled' : ''
      const disabledClass = (!isSelected && atLimit) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-charcoal-50 cursor-pointer'

      return `
        <label class="${chips ? 'district-chip ' : ''}flex items-center gap-2 p-2 rounded-lg ${disabledClass} transition-colors" style="--i: ${Math.min(index, 20)}">
          <input type="checkbox"
                 value="${district.id}"
                 ${checkedAttr}
                 ${disabledAttr}
                 class="${chips ? 'sr-only' : 'h-4 w-4 rounded border-charcoal-300 text-crust-600 focus:ring-crust-500 disabled:opacity-50'}"
                 data-district-name="${this.escapeHtml(district.name)}"
                 data-district-city="${this.escapeHtml(district.city)}"
                 data-action="change->district-targeting#toggleDistrict">
          ${chips ? '<span class="district-chip-check" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-7"/></svg></span>' : ''}
          <span class="text-sm text-charcoal-700">${this.escapeHtml(district.name)}</span>
        </label>
      `
    }).join('')

    this.districtContainerTarget.innerHTML = `
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-1">
        ${checkboxesHtml}
      </div>
    `
  }

  // ============================================================================
  // District Selection
  // ============================================================================

  toggleDistrict(event) {
    const checkbox = event.target
    const id = parseInt(checkbox.value, 10)
    const name = checkbox.dataset.districtName
    const city = checkbox.dataset.districtCity

    // Phase 4: 한도 체크 - 이미 max에 도달했고 새로 선택하려는 경우 차단
    if (checkbox.checked && this.selectedDistricts.size >= this.maxDistrictsValue) {
      checkbox.checked = false
      this.showLimitWarning()
      log.warn('District selection blocked - limit reached', {
        current: this.selectedDistricts.size,
        max: this.maxDistrictsValue
      })
      return
    }

    if (checkbox.checked) {
      this.selectedDistricts.set(id, { id, name, city })
    } else {
      this.selectedDistricts.delete(id)
    }

    this.updateSelectedSummary()
    this.updateHiddenFields()
    this.updateWarning()
    this.updateCheckboxStates()

    log.debug('District toggled', { id, name, selected: checkbox.checked })
  }

  removeDistrict(event) {
    event.preventDefault()
    const id = parseInt(event.currentTarget.dataset.districtId, 10)

    this.selectedDistricts.delete(id)

    // Uncheck the checkbox if visible
    const checkbox = this.districtContainerTarget?.querySelector(`input[value="${id}"]`)
    if (checkbox) {
      checkbox.checked = false
    }

    this.updateSelectedSummary()
    this.updateHiddenFields()
    this.updateWarning()
    this.updateCheckboxStates()

    log.debug('District removed', { id })
  }

  clearAll(event) {
    if (event) event.preventDefault()

    // Clear all selections
    this.selectedDistricts.clear()

    // Uncheck all visible checkboxes
    if (this.hasDistrictContainerTarget) {
      const checkboxes = this.districtContainerTarget.querySelectorAll('input[type="checkbox"]:checked')
      checkboxes.forEach(checkbox => {
        checkbox.checked = false
      })
    }

    this.updateSelectedSummary()
    this.updateHiddenFields()
    this.updateWarning()
    this.updateCheckboxStates()

    log.info('All districts cleared')
  }

  // ============================================================================
  // UI Updates
  // ============================================================================

  updateSelectedSummary() {
    this.updateReach()
    if (!this.hasSelectedCountTarget) return

    if (this.selectedDistricts.size === 0) {
      this.selectedCountTarget.classList.add('hidden')
      return
    }

    this.selectedCountTarget.classList.remove('hidden')
    // Update the span inside the div (first child text node or span)
    const countSpan = this.selectedCountTarget.querySelector('span')
    if (countSpan) {
      countSpan.textContent = `已選擇 ${this.selectedDistricts.size}/${this.maxDistrictsValue} 區`
    }
  }

  updateHiddenFields() {
    if (!this.hasHiddenFieldsTarget) return

    // Clear existing hidden fields
    this.hiddenFieldsTarget.innerHTML = ''

    // Add hidden fields for each selected district
    this.selectedDistricts.forEach((district, id) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = this.fieldNameValue
      input.value = id
      this.hiddenFieldsTarget.appendChild(input)
    })

    // Always include empty value for Rails array handling
    const emptyInput = document.createElement('input')
    emptyInput.type = 'hidden'
    emptyInput.name = this.fieldNameValue
    emptyInput.value = ''
    this.hiddenFieldsTarget.appendChild(emptyInput)
  }

  updateWarning() {
    if (!this.hasWarningMessageTarget) return

    const isNationwide = this.hasNationwideCheckboxTarget && this.nationwideCheckboxTarget.checked
    const hasSelection = this.selectedDistricts.size > 0

    // Show warning only if not nationwide and no districts selected
    this.warningMessageTarget.classList.toggle('hidden', isNationwide || hasSelection)
  }

  // Phase 4: 선택/해제 후 체크박스 상태 업데이트
  updateCheckboxStates() {
    if (!this.hasDistrictContainerTarget) return

    const atLimit = this.selectedDistricts.size >= this.maxDistrictsValue
    const checkboxes = this.districtContainerTarget.querySelectorAll('input[type="checkbox"]')

    checkboxes.forEach(checkbox => {
      const label = checkbox.closest('label')
      if (!checkbox.checked) {
        // 미선택 체크박스: 한도 도달 시 비활성화
        checkbox.disabled = atLimit
        label.classList.toggle('opacity-50', atLimit)
        label.classList.toggle('cursor-not-allowed', atLimit)
        label.classList.toggle('hover:bg-charcoal-50', !atLimit)
        label.classList.toggle('cursor-pointer', !atLimit)
      } else {
        // 선택된 체크박스: 항상 활성화 (해제 가능해야 함)
        checkbox.disabled = false
        label.classList.remove('opacity-50', 'cursor-not-allowed')
        label.classList.add('hover:bg-charcoal-50', 'cursor-pointer')
      }
    })
  }

  // Light one reach tile per chosen district; the newest pops
  updateReach() {
    const count = this.selectedDistricts.size
    this.reachTileTargets.forEach((tile, index) => {
      const lit = index < count
      const wasLit = tile.classList.contains('is-lit')
      tile.classList.toggle('is-lit', lit)
      if (lit && !wasLit) replayAnimation(tile, 'is-new')
    })
    if (this.hasReachMeterTarget) this.reachMeterTarget.classList.toggle('is-full', count >= this.maxDistrictsValue)
  }

  // Phase 4: 한도 도달 경고 표시
  showLimitWarning() {
    if (this.hasReachMeterTarget) replayAnimation(this.reachMeterTarget, 'is-shake')
    if (!this.hasWarningMessageTarget) return

    const warningEl = this.warningMessageTarget
    const originalContent = warningEl.innerHTML

    // 한도 경고 메시지로 임시 변경
    warningEl.innerHTML = `
      <p class="text-xs text-amber-700 flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        最多只能選擇 ${this.maxDistrictsValue} 個區域
      </p>
    `
    warningEl.classList.remove('hidden')

    // 3초 후 원래 내용으로 복원 및 숨김
    setTimeout(() => {
      warningEl.innerHTML = originalContent
      this.updateWarning()
    }, 3000)
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
